
import React, { useState, useEffect } from 'react';
import { ArrowRight, Star, CheckCircle2, ShieldCheck, Clock, ChevronLeft, ChevronRight, X, MapPin } from 'lucide-react';
import { Link } from 'react-router-dom';
import Layout from '../components/Layout';
import Reveal from '../components/Reveal';
import { SERVICES, TESTIMONIALS, PROJECTS, COMPANY_INFO, ASSETS } from '../data';

const Home = () => {
  const [activeTestimonial, setActiveTestimonial] = useState(0);
  const [selectedProject, setSelectedProject] = useState(null);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);

  useEffect(() => {
    document.title = "Jensen & Sørensen ApS";
    let descMeta = document.querySelector('meta[name="description"]');
    if (!descMeta) {
      descMeta = document.createElement('meta');
      descMeta.setAttribute('name', 'description');
      document.head.appendChild(descMeta);
    }
    descMeta.setAttribute('content', '');

    let ogTitleMeta = document.querySelector('meta[property="og:title"]');
    if (!ogTitleMeta) {
      ogTitleMeta = document.createElement('meta');
      ogTitleMeta.setAttribute('property', 'og:title');
      document.head.appendChild(ogTitleMeta);
    }
    ogTitleMeta.setAttribute('content', 'Jensen & Sørensen ApS');

    let ogDescMeta = document.querySelector('meta[property="og:description"]');
    if (!ogDescMeta) {
      ogDescMeta = document.createElement('meta');
      ogDescMeta.setAttribute('property', 'og:description');
      document.head.appendChild(ogDescMeta);
    }
    ogDescMeta.setAttribute('content', '');

    let ogImgMeta = document.querySelector('meta[property="og:image"]');
    if (!ogImgMeta) {
      ogImgMeta = document.createElement('meta');
      ogImgMeta.setAttribute('property', 'og:image');
      document.head.appendChild(ogImgMeta);
    }
    ogImgMeta.setAttribute('content', '');
  }, []);

  useEffect(() => {
    const timer = setInterval(() => {
      setActiveTestimonial((prev) => (prev + 1) % TESTIMONIALS.length);
    }, 6000);
    return () => clearInterval(timer);
  }, []);

  const nextTestimonial = () => {
    setActiveTestimonial((prev) => (prev + 1) % TESTIMONIALS.length);
  };

  const prevTestimonial = () => {
    setActiveTestimonial((prev) => (prev - 1 + TESTIMONIALS.length) % TESTIMONIALS.length);
  };

  const openProject = (project) => {
    setSelectedProject(project);
    setCurrentImageIndex(0);
    document.body.style.overflow = 'hidden';
  };

  const closeProject = () => {
    setSelectedProject(null);
    document.body.style.overflow = 'auto';
  };

  const nextImage = (e) => {
    e.stopPropagation();
    if (selectedProject) {
      setCurrentImageIndex((prev) => (prev + 1) % selectedProject.images.length);
    }
  };

  const prevImage = (e) => {
    e.stopPropagation();
    if (selectedProject) {
      setCurrentImageIndex((prev) => (prev - 1 + selectedProject.images.length) % selectedProject.images.length);
    }
  };

  return (
    <Layout>
      {/* Hero Section */}
      <section className="relative h-[800px] flex items-center overflow-hidden bg-slate-900">
        <div className="absolute inset-0 z-0">
          <video 
            src="https://assets.auxodev.dk/assets/650c050a-1b3b-43e1-83de-064d04c7258f/d92c18ea-46a1-47f8-b16a-14affa9957a5.mp4"
            autoPlay
            loop
            muted
            playsInline
            className="w-full h-full object-cover opacity-60"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-slate-900 via-slate-900/40 to-transparent" />
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center mt-16">
          <Reveal>
            <h1 className="text-4xl md:text-6xl lg:text-7xl font-bold text-white tracking-tighter mb-6 leading-none break-words">
              DIT BYGGERI. <br />
              <span className="text-amber-500">VORES STOLTHED.</span>
            </h1>
            <p className="text-xl text-slate-300 max-w-2xl mx-auto mb-10 leading-relaxed font-light">
              Hos Jensen & Sørensen forener vi traditionelt tømrerhåndværk med moderne løsninger. 
              Vi bygger for fremtiden i Aalborg og omegn.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link 
                to="/projekter" 
                className="px-8 py-4 bg-amber-500 text-white font-bold hover:bg-amber-600 transition-all flex items-center justify-center gap-2 group"
              >
                SE VORES PROJEKTER
                <ArrowRight size={20} className="group-hover:translate-x-1 transition-transform" />
              </Link>
              <Link 
                to="/kontakt" 
                className="px-8 py-4 bg-white/10 text-white backdrop-blur-md font-bold hover:bg-white/20 transition-all border border-white/10 text-center"
              >
                KONTAKT OS NU
              </Link>
            </div>
          </Reveal>
        </div>
      </section>

      {/* Trust Badges */}
      <div className="bg-slate-950 py-10 border-b border-white/5">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Reveal>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
              <div className="flex items-center gap-4 text-white/70">
                <ShieldCheck className="text-amber-500" size={32} />
                <span className="text-sm font-bold uppercase tracking-widest">Garantiordning</span>
              </div>
              <div className="flex items-center gap-4 text-white/70">
                <Clock className="text-amber-500" size={32} />
                <span className="text-sm font-bold uppercase tracking-widest">Overholder tidsplan</span>
              </div>
              <div className="flex items-center gap-4 text-white/70">
                <Star className="text-amber-500" size={32} />
                <span className="text-sm font-bold uppercase tracking-widest">Kvalitetsmaterialer</span>
              </div>
              <div className="flex items-center gap-4 text-white/70">
                <CheckCircle2 className="text-amber-500" size={32} />
                <span className="text-sm font-bold uppercase tracking-widest">Præcis finish</span>
              </div>
            </div>
          </Reveal>
        </div>
      </div>

      {/* Services Section */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Reveal>
            <div className="text-center mb-16">
              <h2 className="text-xs font-bold text-amber-500 tracking-[0.3em] uppercase mb-4">Hvad vi tilbyder</h2>
              <h3 className="text-xl sm:text-2xl md:text-4xl lg:text-5xl font-bold text-slate-900 tracking-tight">KVALITETSHÅNDVÆRK TIL ALLE BEHOV</h3>
            </div>
          </Reveal>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-6 auto-rows-[240px] gap-4">
            {SERVICES.map((service, idx) => {
              const spans = [
                'md:col-span-2 lg:col-span-4 lg:row-span-2', // Nyt Tag (4x2)
                'lg:col-span-2 lg:row-span-1', // Træterrasse (2x1)
                'lg:col-span-2 lg:row-span-1', // Carporte (2x1)
                'lg:col-span-2 lg:row-span-1', // Vinduer (2x1)
                'lg:col-span-2 lg:row-span-1', // Gulve (2x1)
                'md:col-span-2 lg:col-span-2 lg:row-span-1', // Renovering (2x1)
              ];

              return (
                <Reveal key={service.id} delay={idx * 100} width={spans[idx] || 'lg:col-span-2'}>
                  <Link 
                    to={`/service/${service.slug}`}
                    className="group relative overflow-hidden rounded-2xl shadow-sm hover:shadow-xl transition-all duration-500 block h-full"
                  >
                    <img 
                      src={service.image} 
                      alt={service.title} 
                      className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-slate-900/90 via-slate-900/20 to-transparent" />
                    <div className="absolute bottom-0 left-0 p-8 w-full transform translate-y-2 group-hover:translate-y-0 transition-transform duration-500">
                      <h4 className="font-bold text-white mb-2 text-2xl">
                        {service.title}
                      </h4>
                      <p className="text-white/70 text-sm mb-4 line-clamp-2 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                        {service.description}
                      </p>
                      <span className="inline-flex items-center text-amber-500 font-bold text-xs tracking-widest uppercase">
                        Læs mere <ArrowRight size={14} className="ml-2" />
                      </span>
                    </div>
                  </Link>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* Vores Projekter & Kunder Fortæller */}
      <section className="py-24 bg-slate-900 border-y border-white/5">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-16 lg:gap-24 items-start">
            
            {/* Left: Testimonials Slider */}
            <div className="lg:col-span-5 flex flex-col h-full">
              <Reveal>
                <div className="mb-12">
                  <h2 className="text-amber-500 font-bold text-xs tracking-widest uppercase mb-4 flex items-center gap-2">
                    <span className="w-8 h-[1px] bg-amber-500"></span> Kunder fortæller
                  </h2>
                  <h3 className="text-3xl md:text-4xl font-bold text-white tracking-tight break-words">TILLID GENNEM <br />GODT HÅNDVÆRK</h3>
                </div>
              </Reveal>

              <Reveal delay={200}>
                <div className="relative flex-grow flex flex-col justify-center">
                  <div className="overflow-hidden relative min-h-[340px] flex items-center">
                    {TESTIMONIALS.map((t, idx) => (
                      <div 
                        key={t.id}
                        className={`absolute inset-0 transition-all duration-700 ease-in-out flex flex-col justify-center ${
                          idx === activeTestimonial 
                            ? 'opacity-100 translate-x-0' 
                            : 'opacity-0 translate-x-8 pointer-events-none'
                        }`}
                      >
                        <div className="p-10 relative">
                          <div className="flex gap-1 text-amber-500 mb-8">
                            {[...Array(5)].map((_, i) => <Star key={i} size={16} fill="currentColor" />)}
                          </div>
                          <p className="text-2xl font-light text-slate-200 italic mb-8 leading-relaxed">
                            "{t.text}"
                          </p>
                          <div className="flex items-center gap-4">
                            <div className="w-12 h-[1px] bg-amber-500"></div>
                            <div>
                              <h4 className="text-white font-bold text-lg">{t.name}</h4>
                              <p className="text-slate-500 text-xs uppercase tracking-widest mt-1">{t.project}</p>
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>

                  {/* Slider Controls */}
                  <div className="flex items-center gap-6 mt-8">
                    <div className="flex gap-2">
                      {TESTIMONIALS.map((_, idx) => (
                        <button
                          key={idx}
                          onClick={() => setActiveTestimonial(idx)}
                          className={`h-1 transition-all duration-300 rounded-full ${
                            idx === activeTestimonial ? 'w-8 bg-amber-500' : 'w-4 bg-white/20'
                          }`}
                          aria-label={`Gå til anmeldelse ${idx + 1}`}
                        />
                      ))}
                    </div>
                    <div className="flex gap-2 ml-auto">
                      <button 
                        onClick={prevTestimonial}
                        className="p-3 rounded-full border border-white/10 text-white hover:bg-amber-500 hover:border-amber-500 transition-all"
                      >
                        <ChevronLeft size={20} />
                      </button>
                      <button 
                        onClick={nextTestimonial}
                        className="p-3 rounded-full border border-white/10 text-white hover:bg-amber-500 hover:border-amber-500 transition-all"
                      >
                        <ChevronRight size={20} />
                      </button>
                    </div>
                  </div>
                </div>
              </Reveal>
            </div>

            {/* Right: Featured Projects Grid */}
            <div className="lg:col-span-7">
              <Reveal>
                <div className="mb-12">
                  <h2 className="text-amber-500 font-bold text-xs tracking-widest uppercase mb-4">Vores arbejde</h2>
                  <h3 className="text-3xl md:text-4xl font-bold text-white tracking-tight break-words">UDVALGTE PROJEKTER</h3>
                </div>
              </Reveal>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {PROJECTS.slice(0, 4).map((project, idx) => (
                  <Reveal key={project.id} delay={idx * 150}>
                    <div 
                      className="group relative overflow-hidden rounded-xl bg-slate-800 cursor-pointer"
                      onClick={() => openProject(project)}
                    >
                      <div className="aspect-[4/3] overflow-hidden">
                        <img 
                          src={project.image} 
                          alt={project.title} 
                          className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                        />
                      </div>
                      <div className="absolute inset-0 bg-gradient-to-t from-slate-950 via-slate-950/20 to-transparent opacity-90" />
                      <div className="absolute bottom-0 left-0 p-6 w-full">
                        <p className="text-amber-500 text-xs font-bold uppercase tracking-widest mb-2">{project.location}</p>
                        <h5 className="text-white font-bold text-lg">{project.title}</h5>
                      </div>
                    </div>
                  </Reveal>
                ))}
              </div>
              
              <Reveal delay={400}>
                <div className="mt-12 flex justify-end">
                  <Link 
                    to="/projekter" 
                    className="group flex items-center gap-3 text-white font-bold tracking-tight hover:text-amber-500 transition-colors"
                  >
                    SE ALLE VORES PROJEKTER 
                    <span className="w-10 h-10 rounded-full border border-white/20 flex items-center justify-center group-hover:border-amber-500 group-hover:bg-amber-500 transition-all">
                      <ArrowRight size={18} />
                    </span>
                  </Link>
                </div>
              </Reveal>
            </div>

          </div>
        </div>
      </section>

      {/* Project Gallery Modal */}
      {selectedProject && (
        <div 
          className="fixed inset-0 z-[100] flex items-center justify-center bg-slate-950/95 p-4 md:p-10"
          onClick={closeProject}
        >
          <button 
            className="absolute top-6 right-6 text-white/70 hover:text-white transition-colors z-[110]"
            onClick={closeProject}
          >
            <X size={40} />
          </button>

          <div className="relative w-full max-w-6xl h-full flex flex-col items-center justify-center gap-8" onClick={(e) => e.stopPropagation()}>
            <div className="relative w-full flex-1 flex items-center justify-center group">
              <button 
                className="absolute left-0 text-white/50 hover:text-white transition-colors p-4 z-10"
                onClick={prevImage}
              >
                <ChevronLeft size={48} />
              </button>
              
              <img 
                src={selectedProject.images[currentImageIndex]} 
                alt={`${selectedProject.title} - ${currentImageIndex + 1}`}
                className="max-w-full max-h-full object-contain shadow-2xl rounded-lg"
              />

              <button 
                className="absolute right-0 text-white/50 hover:text-white transition-colors p-4 z-10"
                onClick={nextImage}
              >
                <ChevronRight size={48} />
              </button>
            </div>

            <div className="text-center text-white">
              <h2 className="text-2xl md:text-3xl font-bold tracking-tight mb-2">{selectedProject.title}</h2>
              <p className="text-amber-500 font-bold uppercase tracking-widest text-sm flex items-center justify-center gap-2">
                <MapPin size={16} /> {selectedProject.location}
              </p>
              <div className="flex gap-2 mt-6 justify-center">
                {selectedProject.images.map((_, idx) => (
                  <button
                    key={idx}
                    onClick={() => setCurrentImageIndex(idx)}
                    className={`w-2 h-2 rounded-full transition-all ${currentImageIndex === idx ? 'bg-amber-500 w-8' : 'bg-white/20 hover:bg-white/40'}`}
                  />
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Final SEO Text Section */}
      <section className="py-24 bg-slate-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <Reveal>
            <h2 className="text-2xl md:text-3xl font-bold text-slate-900 mb-8 tracking-tight break-words">DIN LOKALE TØMRER I AALBORG & NORDJYLLAND</h2>
            <div className="text-slate-600 space-y-6 leading-relaxed text-lg font-light">
              <p>
                Søger du en pålidelig og erfaren tømrer i Aalborg, Nørresundby, Brønderslev eller Støvring? Jensen & Sørensen ApS er din dedikerede partner, når det kommer til alt inden for tømrerarbejde. Vi vægter den tætte dialog med kunden højt, og sikrer os altid, at du er med hele vejen fra idé til færdigt byggeri.
              </p>
              <p>
                Vi er specialister i alt fra <strong>nyt tag</strong> og <strong>carporte</strong> til skræddersyede <strong>træterrasser</strong> og omfattende <strong>renoveringer</strong>. Vores team består af passionerede tømrere, der aldrig går på kompromis med kvaliteten af deres arbejde.
              </p>
            </div>
            <div className="mt-12">
               <Link to="/kontakt" className="inline-block px-10 py-5 bg-slate-900 text-white font-bold hover:bg-slate-800 transition-colors">
                 SKAL VI HJÆLPE DIG?
               </Link>
            </div>
          </Reveal>
        </div>
      </section>
    </Layout>
  );
};

export default Home;
